import React, { useState, useEffect } from 'react';
import MobileNotice from './components/MobileNotice';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import UserCard from './components/UserCard';
import ProfilePrompt from './components/ProfilePrompt';
import UserIcon from './components/UserIcon';
import ProfilePage from './components/ProfilePage';
import SearchForm from './components/SearchForm';
import EmailSignIn from './components/EmailSignIn';
import PasswordSignIn from './components/PasswordSignIn';
import EmailLinkVerify from './components/EmailLinkVerify';
import RegisterForm from './components/RegisterCard';
import CompleteProfile from './components/CompleteProfile';
import AdminPage from './components/admin/AdminPage';
import ProtectedRoute from './components/ProtectedRoute';
import Loading from './components/Loading';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { AuthProvider, useAuth } from './context/AuthContext';
import './styles/tailwind.css';
import EditProfile from './components/EditProfile'; // Import the EditProfile component
import { Link } from 'react-router-dom';
import { auth, db } from './firebase'; // Import the Firestore instance
import { collection, query, where, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useAuthorizationCheck, UnauthorizedAccess } from './components/Unauthorized';
import { Input } from './components/ui/input';
import { Toaster } from './components/ui/toaster';
import UserIconV2 from './components/UserIconV2';
import { mergedMap } from "./mapping";
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import SignUpCard from './components/SignUpCard';
import HelpButton from './components/HelpButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './components/ui/dropdown-menu';
import { ChevronDown, ArrowUpNarrowWide, ArrowDownNarrowWide, ArrowDownAZ } from 'lucide-react';
import { Button } from './components/ui/button';
import { AdminRoute } from './components/AdminRoute';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient()

function App() {
  return (
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <Router>
              <Main />
          </Router>
        </QueryClientProvider>
      </AuthProvider>
      
  );
}

function Main() {
  const location = useLocation();
  const { currentUser: authUser } = useAuth(); // Get user from AuthContext
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [degrees, setDegrees] = useState(new Set());
  const [companies, setCompanies] = useState(new Set());
  const [isOpen, setIsOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [filters, setFilters] = useState({
    keyword: '',
    academics: '',
    sector: '',
    hometown: '',
    currentLocation: '',
    role: '',
    company: '',
    nationalities: [],
    gradYearStart: '',
    gradYearEnd: ''
  });
  const [sortOrder, setSortOrder] = useState('none'); // 'none', 'asc', or 'desc'
  
  // Update handleClear function
  const handleClear = () => {
    setFilters({
      keyword: '',
      academics: '',
      sector: '',
      hometown: '',
      currentLocation: '',
      role: '',
      company: '',
      nationalities: [],
      gradYearStart: '',
      gradYearEnd: ''
    });
  };
  
  const handleLinkClick = (e, path) => {
    e.preventDefault();
    // Update the URL without full page navigation
    window.history.pushState({}, '', path);
    setCurrentPath(path);
    setIsOpen(true);
  };

  // Handle browser back/forward buttons
  React.useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
      setIsOpen(false);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // First try to fetch user by UID
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          
          if (userDoc.exists()) {
            setCurrentUser({ id: user.uid, ...userDoc.data() });
          } else {
            // If no document found by UID, try to find by email
            console.log('No user found by UID, trying email...');
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('email', '==', user.email));
            const querySnapshot = await getDocs(q);
  
            if (!querySnapshot.empty) {
              // Found user by email - get their data
              const userData = querySnapshot.docs[0].data();
              const oldDocId = querySnapshot.docs[0].id;
  
              // Create new document with auth UID
              await setDoc(doc(db, 'users', user.uid), {
                ...userData,
                email: user.email
              });
  
              // Delete the old document
              await deleteDoc(doc(db, 'users', oldDocId));
  
              // Set current user with new UID
              setCurrentUser({ id: user.uid, ...userData });
              console.log('User document migrated to use UID');
            } else {
              console.log('No user found by email either.');
            }
          }
          
          // Always fetch all users
          await fetchUsers();
          
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
      setLoading(false);
    });
  
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (authUser) {
        try {
          // First fetch the current user's data using auth UID
          const userRef = doc(db, 'users', authUser.id);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            setCurrentUser({ id: authUser.id, ...userDoc.data() });
          }
          
          // Fetch all users
          await fetchUsers();
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    };

    fetchUserData();
  }, [authUser]); // Depend on authUser changes

  const fetchUsers = async () => {
    const usersRef = collection(db, "users");
    const q = query(
      usersRef,
      where("name", "!=", "")
    );
    const querySnapshot = await getDocs(q);
    // Filter out null names in JavaScript after getting the results
    const usersData = querySnapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter(user => user.name != null);  // Remove any null names post-query
      
    setUsers(usersData);
    usersData.map((doc) => {
      if (doc.currentCompany !== "") {
        companies.add(doc.currentCompany);
      }
      degrees.add(doc.major)
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const mobileCheck = function() {
    let check = false;
    (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
    return check;
  };

  /*
  if (mobileCheck()) {
    return <MobileNotice />
  }
    */

  if (loading) {
    return <Loading />;
  }

  const filteredUsers = users.filter((user) => {
    // Check nationality filter
    const hasMatchingNationality = filters.nationalities.length === 0 || 
      (user.nationalities && user.nationalities.some(nationality => 
        filters.nationalities.includes(nationality)
      ));
  
    let gStart = 1974;
    if (parseInt(filters.gradYearStart) > 1974) {
      gStart = parseInt(filters.gradYearStart);
    }

    let gEnd = 2060;
    if (parseInt(filters.gradYearEnd) < 2060) {
      gEnd = parseInt(filters.gradYearEnd);
    }

    let gradYearInRange = false;

    if (!isNaN(parseInt(user.gradYear))) {
      gradYearInRange = (parseInt(user.gradYear) >= gStart && parseInt(user.gradYear) <= gEnd);
    }

    if (gStart <= 1974 && gEnd >= 2060) {
      gradYearInRange = true;
    }

    if (user.name == "Aidan Young"){
      console.log(parseInt(user.gradYear), gStart, gEnd)
      console.log(parseInt(user.gradYear) >= gStart, parseInt(user.gradYear) <= gEnd)
    }
    // Check graduation year range
    
    console.log(user.majors)
  
    // Check keyword against name, blurb, and extracurriculars
    const keywordMatch = !filters.keyword || 
      (user.name?.toLowerCase().includes(filters.keyword.toLowerCase()) ||
       user.blurb?.toLowerCase().includes(filters.keyword.toLowerCase()) ||
       user.extracurriculars?.toLowerCase().includes(filters.keyword.toLowerCase()));
    // Use includes() for all string searches
    return (
      keywordMatch &&
      ((!filters.academics || (user.majors && user.majors.includes(filters.academics))) ||
      (!filters.academics || (user.minors && user.minors.includes(filters.academics))) ||
      (!filters.academics || (user.certifications && user.certifications.includes(filters.academics)))) &&
      (!filters.sector || (user.sectors && user.sectors?.includes(filters.sector))) &&
      (!filters.hometown || user.hometown?.toLowerCase().includes(filters.hometown.toLowerCase())) &&
      (!filters.currentLocation || user.location?.toLowerCase().includes(filters.currentLocation.toLowerCase())) &&
      (!filters.role || user.currentRole?.toLowerCase().includes(filters.role.toLowerCase())) &&
      (!filters.company || user.currentCompany?.toLowerCase().includes(filters.company.toLowerCase())) &&
      hasMatchingNationality &&
      gradYearInRange
    );
  });

  const handleSort = (order) => {
    setSortOrder(order);
  };

  const sortedAndFilteredUsers = [...filteredUsers].sort((a, b) => {
    if (sortOrder === 'none') return 0;
    const yearA = parseInt(a.gradYear) || 0;
    const yearB = parseInt(b.gradYear) || 0;
    return sortOrder === 'asc' ? yearA - yearB : yearB - yearA;
  });

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-8">
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <div className="w-full sm:container sm:mx-auto sm:px-4">
              <header className="w-full justify-between items-center mb-8 z-[4999]">
                <div className="flex w-full gap-4 items-center justify-between">
                  
                  <Link to="/" className="text-4xl font-bold shrink-0">
                    {mobileCheck() ? <img className="w-12 h-12" src="/android-chrome-192x192.png"></img> : "WM Internal"}
                  </Link>
                  <div className="flex flex-1 flex-row gap-4">
                    {location.pathname === '/' && (
                      <div className="flex flex-1 shrink-0 min-w-0 mx-auto gap-2">
                        <div className="flex-1">
                          <SearchForm 
                            filters={filters} 
                            handleChange={handleChange} 
                            handleClear={handleClear} 
                            degrees={degrees} 
                            companies={companies} 
                          />
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="gap-2 shrink-0">
                              Sort
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="z-[5000]">
                            <DropdownMenuItem onClick={() => handleSort('none')} className="gap-2">
                            <ArrowDownAZ className="h-4 w-4" />
                              Alphabetic
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSort('asc')} className="gap-2">
                              <ArrowUpNarrowWide className="h-4 w-4" />
                              Grad Year Ascending
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSort('desc')} className="gap-2">
                              <ArrowDownNarrowWide className="h-4 w-4" />
                              Grad Year Descending
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                    <UserIcon user={currentUser} className="shrink-0" />
                  </div>
                </div>
                </header>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sortedAndFilteredUsers.map((user, index) => (
                  <UserCard key={user.id} user={user} index={{ zIndex: sortedAndFilteredUsers.length - index }} handleLink={handleLinkClick} />
                ))}
                </div>
                
              </div>
              <ProfilePrompt currentUser={currentUser} />
              <HelpButton />
              {isOpen && (
                <ProfilePage users={users} isModal={true} userID={currentPath.split("/")[currentPath.split("/").length-1]} setIsOpen={setIsOpen} setPath={setCurrentPath}/> 
              )}
              
            </ProtectedRoute>
          }
        />
        <Route path="/profile/:id" element={<ProtectedRoute ><ProfilePage users={users} isModal={false}/></ProtectedRoute>} />
        <Route path="/login" element={<>
            <EmailSignIn />
            <SignUpCard />
          </>} />
        <Route path="/register" element={<RegisterForm />} />
        <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
        <Route path="/verify" element={<EmailLinkVerify />} />
        <Route path="/edit-profile" element={<ProtectedRoute ><EditProfile user={currentUser} setCurrentUser={setCurrentUser} setUsers={setUsers} /></ProtectedRoute>} />
        <Route path="/complete-profile" element={<ProtectedRoute ><CompleteProfile /></ProtectedRoute>} />
        <Route path="/unauthorized" element={<UnauthorizedAccess auth={auth}/>} />
      </Routes>
      <Toaster />
    </div>
  );
}

export default App;
